<template>
  <div class="row g-4">
    <div class="col-12 mb-3">
      <router-link to="/work/buckets" class="btn btn-secondary btn-no-r">
        <span><BaseIcon name="active" /> {{ $t("work.dashboard") }}</span>
      </router-link>
      <router-link
        to="/work/tracking"
        class="btn btn-no-l btn-no-r btn-secondary"
      >
        <span><BaseIcon name="timer" /> {{ $t("work.timeTracking") }}</span>
      </router-link>
      <router-link
        to="/work/tracking/performance"
        class="btn btn-no-l btn-primary"
      >
        <span><BaseIcon name="timer" /> {{ $t("work.timeTracking") }}</span>
      </router-link>
    </div>
    <div class="co-12 performance-dashboard">
      <!-- KPI Summary -->
      <div class="kpi-grid mb-4">
        <div class="kpi-card text-center p-3">
          <div class="kpi-label mb-1">{{ $t("work.averageTime") }}</div>
          <div class="kpi-value">
            {{ formatDuration(kpi.avg_time ?? 0) }}
          </div>
        </div>
        <div class="kpi-card text-center p-3">
          <div class="kpi-label mb-1">{{ $t("work.tasksProcessed") }}</div>
          <div class="kpi-value">{{ kpi.tasks_processed ?? 0 }}</div>
        </div>
        <div class="kpi-card text-center p-3">
          <div class="kpi-label mb-1">{{ $t("work.efficiencyScore") }}</div>
          <div class="kpi-value">{{ kpi.efficiency_score ?? 0 }}%</div>
        </div>
        <div class="kpi-card text-center p-3">
          <div class="kpi-label mb-1">{{ $t("work.activeUsers") }}</div>
          <div class="kpi-value">{{ kpi.active_users ?? 0 }}</div>
        </div>
      </div>

      <!-- ANALYTICS GRID -->
      <div class="analytics-grid">
        <!-- PRODUCT PERFORMANCE -->
        <div class="panel">
          <div class="panel-header">
            <h3>{{ $t("work.productPerformance") }}</h3>
          </div>

          <div class="table-responsive">
            <table class="heat-table">
              <thead>
                <tr>
                  <th>{{ $t("work.product") }}</th>
                  <th class="cell">{{ $t("work.income") }}</th>
                  <th class="cell">{{ $t("work.picking") }}</th>
                  <th class="cell">{{ $t("work.packing") }}</th>
                  <th class="cell">{{ $t("work.return") }}</th>
                  <th class="cell total">{{ $t("work.total") }}</th>
                  <th class="cell">{{ $t("work.count") }}</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="item in productPerformance" :key="item.product">
                  <td class="label text-truncate" style="max-width: 150px">
                    {{ item.product }}
                  </td>
                  <td class="cell heat">{{ formatDuration(item.income) }}</td>
                  <td class="cell heat">{{ formatDuration(item.picking) }}</td>
                  <td class="cell heat">{{ formatDuration(item.packing) }}</td>
                  <td class="cell heat">{{ formatDuration(item.return) }}</td>
                  <td class="cell total">{{ formatDuration(item.total) }}</td>
                  <td class="cell">{{ item.products_count }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- CATEGORY PERFORMANCE -->
        <div class="panel">
          <div class="panel-header">
            <h3>{{ $t("work.categoryPerformance") }}</h3>
          </div>

          <div class="metric-list">
            <div
              v-for="cat in categoryPerformance"
              :key="cat.id"
              class="metric-row cursor-pointer p-2 rounded"
              :class="{
                'bg-light active-filter': filters.category_id === cat.id,
              }"
              @click="selectCategory(cat.id)"
            >
              <span>{{ cat.name }}</span>

              <div class="metric-bar">
                <div class="performance excellent" style="width: 100%"></div>
              </div>

              <strong>{{ formatDuration(cat.total) }}</strong>

              <div
                v-if="filters.category_id === cat.id"
                class="w-100 mt-2 small text-muted border-top pt-2"
              >
                <div class="d-flex justify-content-between">
                  <span
                    >{{ $t("work.income") }}:
                    {{ formatDuration(cat.income) }}</span
                  >
                  <span
                    >{{ $t("work.picking") }}:
                    {{ formatDuration(cat.picking) }}</span
                  >
                  <span
                    >{{ $t("work.packing") }}:
                    {{ formatDuration(cat.packing) }}</span
                  >
                  <span
                    >{{ $t("work.return") }}:
                    {{ formatDuration(cat.return) }}</span
                  >
                  <span>{{ $t("work.count") }}: {{ cat.products_count }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- PARTNERS -->
        <div class="panel">
          <div class="panel-header">
            <h3>{{ $t("work.partnerPerformance") }}</h3>
          </div>

          <div class="partner-list">
            <div
              v-for="partner in partnerPerformance"
              :key="partner.id"
              class="partner-item cursor-pointer p-2 rounded"
              :class="{
                'bg-light active-filter': filters.partner_id === partner.id,
              }"
              @click="selectPartner(partner.id)"
            >
              <div>
                <strong>{{ partner.name }}</strong>
                <small
                  >{{ partner.products_count }} {{ $t("work.products") }}</small
                >
              </div>

              <div class="partner-score">
                {{ formatDuration(partner.total) }}
              </div>

              <div
                v-if="filters.partner_id === partner.id"
                class="w-100 mt-2 small text-muted border-top pt-2"
              >
                <div class="d-flex justify-content-between">
                  <span
                    >{{ $t("work.income") }}:
                    {{ formatDuration(partner.income) }}</span
                  >
                  <span
                    >{{ $t("work.picking") }}:
                    {{ formatDuration(partner.picking) }}</span
                  >
                  <span
                    >{{ $t("work.packing") }}:
                    {{ formatDuration(partner.packing) }}</span
                  >
                  <span
                    >{{ $t("work.return") }}:
                    {{ formatDuration(partner.return) }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- TOP USERS -->
        <div class="panel">
          <div class="panel-header">
            <h3>{{ $t("work.userPerformance") }}</h3>
          </div>

          <div class="leaderboard">
            <div
              v-for="user in userPerformance"
              :key="user.id"
              class="leaderboard-item cursor-pointer p-2 rounded"
              :class="{ 'bg-light active-filter': filters.user_id === user.id }"
              @click="selectUser(user.id)"
            >
              <div class="avatar">
                {{ user.name.substring(0, 2).toUpperCase() }}
              </div>

              <div class="leaderboard-info">
                <strong>{{ user.name }}</strong>
                <span>{{ user.products_count }} {{ $t("work.tasks") }}</span>
              </div>

              <div class="leaderboard-score">
                {{ formatDuration(user.total) }}
              </div>

              <div
                v-if="filters.user_id === user.id"
                class="w-100 mt-2 small text-muted border-top pt-2"
              >
                <div class="d-flex justify-content-between">
                  <span
                    >{{ $t("work.income") }}:
                    {{ formatDuration(user.income) }}</span
                  >
                  <span
                    >{{ $t("work.picking") }}:
                    {{ formatDuration(user.picking) }}</span
                  >
                  <span
                    >{{ $t("work.packing") }}:
                    {{ formatDuration(user.packing) }}</span
                  >
                  <span
                    >{{ $t("work.return") }}:
                    {{ formatDuration(user.return) }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- USER DRILLDOWN -->
      <div class="panel drilldown">
        <div class="panel-header">
          <h3>{{ $t("work.userDrillDown") }}</h3>

          <input
            type="text"
            class="search-input"
            :placeholder="$t('work.searchUser')"
          />
        </div>

        <table class="analytics-table">
          <thead>
            <tr>
              <th>{{ $t("work.user") }}</th>
              <th>{{ $t("work.income") }}</th>
              <th>{{ $t("work.picking") }}</th>
              <th>{{ $t("work.packing") }}</th>
              <th>{{ $t("work.return") }}</th>
              <th>{{ $t("work.count") }}</th>
              <th>{{ $t("work.total") }}</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="user in userPerformance" :key="user.id">
              <td>{{ user.name }}</td>
              <td>{{ formatDuration(user.income) }}</td>
              <td>{{ formatDuration(user.picking) }}</td>
              <td>{{ formatDuration(user.packing) }}</td>
              <td>{{ formatDuration(user.return) }}</td>
              <td>{{ user.products_count }}</td>
              <td>
                <span class="score-badge neutral">{{
                  formatDuration(user.total)
                }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- SECTION DIVIDER -->
      <div class="section-divider">
        <span>{{ $t("work.eventPerformance") }}</span>
      </div>
      <div class="row">
        <div
          v-for="category in eventPerformance"
          :key="category.event_name"
          class="col-lg-4 mb-4"
        >
          <div class="card p-3 h-100">
            <div class="category-title">{{ category.event_name }}</div>
            <div class="category-metric">
              {{ $t("work.average") }}:
              {{ formatDuration(category.overall_avg) }}
            </div>
            <div class="graph-wrapper">
              <div class="graph-scale">
                <div
                  v-for="user in category.users"
                  :key="user.user_id"
                  class="graph-marker"
                  :style="{
                    left: calculatePosition(user.avg_duration, category) + '%',
                  }"
                >
                  <div
                    class="graph-dot"
                    :class="getDotColor(user.avg_duration, category)"
                  ></div>
                  <div class="graph-label">
                    {{ user.name }} {{ formatDuration(user.avg_duration) }}
                  </div>
                </div>
              </div>
              <div class="scale-labels">
                <span>{{ $t("work.fast") }}</span>
                <span>{{ $t("work.slow") }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import date from "../../../modules/date";
import http from "@/modules/http";
import BaseIcon from "../../../components/icons/BaseIcon.vue";

export default {
  name: "UserPerformance",
  components: { BaseIcon },
  data() {
    return {
      kpi: {},
      productPerformance: [],
      categoryPerformance: [],
      partnerPerformance: [],
      userPerformance: [],
      eventPerformance: [],
      filters: {
        user_id: null,
        partner_id: null,
        category_id: null,
      },
    };
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    load() {
      const url = this.id ? "/work/timing/" + this.id : "/work/timing";

      let query = "";
      if (this.filters.category_id) {
        query +=
          (query ? "&" : "?") + "category_id=" + this.filters.category_id;
      }
      if (this.filters.partner_id) {
        query += (query ? "&" : "?") + "partner_id=" + this.filters.partner_id;
      }
      if (this.filters.user_id) {
        query += (query ? "&" : "?") + "user_id=" + this.filters.user_id;
      }

      http.fetch(url + query).then((data) => {
        this.kpi = data.kpi;
        this.productPerformance = data.product_performance;
        this.categoryPerformance = data.category_performance;
        this.partnerPerformance = data.partner_performance;
        this.userPerformance = data.user_performance;
        this.eventPerformance = data.event_performance;
      });
    },
    selectCategory(categoryId) {
      this.filters.category_id =
        this.filters.category_id === categoryId ? null : categoryId;
      this.load();
    },
    selectPartner(partnerId) {
      this.filters.partner_id =
        this.filters.partner_id === partnerId ? null : partnerId;
      this.load();
    },
    selectUser(userId) {
      this.filters.user_id = this.filters.user_id === userId ? null : userId;
      this.load();
    },
    formatDate(val) {
      return date.format(val, false, false);
    },
    formatDuration(seconds) {
      if (seconds < 60) {
        return Math.round(seconds * 10) / 10 + "s";
      }
      const minutes = Math.round((seconds / 60) * 10) / 10;
      return minutes + "m";
    },
    calculatePosition(val, category) {
      const avg = category.overall_avg;
      if (avg === 0) return 50;

      // Map value relative to average.
      // 0.5 * avg -> 0% (Fast)
      // avg       -> 50% (Average)
      // 1.5 * avg -> 100% (Slow)
      let position = (val / avg) * 50;

      if (position < 5) position = 5;
      if (position > 95) position = 95;

      return position;
    },
    getDotColor(val, category) {
      const avg = category.overall_avg;
      if (val < avg * 0.9) return "dot-green";
      if (val > avg * 1.3) return "dot-red";
      if (val > avg * 1.1) return "dot-orange";
      return "dot-blue";
    },
  },
};
</script>
<style scoped>
.category-title {
  font-weight: 700;
  font-size: 1.2rem;
}

.category-metric {
  font-size: 0.9rem;
  color: #6b7280;
}

.graph-wrapper {
  margin-top: 2rem;
  margin-bottom: 2em;
}

.graph-scale {
  position: relative;
  height: 14px;
  background: linear-gradient(90deg, #16a34a, #eab308, #dc2626);
  border-radius: 10px;
}

.graph-marker {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.graph-dot {
  margin: 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 4px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.dot-green {
  background: #16a34a;
}
.dot-blue {
  background: #6366f1;
}
.dot-orange {
  background: #f59e0b;
}
.dot-red {
  background: #dc2626;
}

.graph-label {
  font-size: 0.75rem;
  margin-top: 8px;
  max-width: 5em;
  position: absolute;
  background: #fff;
}

.scale-labels {
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
  margin-top: 0.8rem;
  color: #6b7280;
}

.dashboard-actions {
  display: flex;
  gap: 12px;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

.kpi-card {
  background: white;
  border-radius: 20px;
  padding: 22px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.04);
}

.kpi-label {
  color: #64748b;
  font-size: 0.85rem;
}

.kpi-value {
  font-size: 2rem;
  font-weight: 800;
  margin: 8px 0;
  color: #0f172a;
}

.kpi-change {
  color: #64748b;
  font-size: 0.85rem;
}

.kpi-change.positive {
  color: #16a34a;
}

.analytics-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 24px;
}

.panel {
  background: white;
  border-radius: 20px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 6px 24px rgba(15, 23, 42, 0.04);
  padding: 24px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.panel-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.metric-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.metric-row {
  display: grid;
  grid-template-columns: 120px 1fr 60px;
  gap: 12px;
  align-items: center;
}

.metric-row span {
  font-size: 0.9rem;
}

.metric-bar {
  height: 10px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.metric-bar div {
  height: 100%;
  background: linear-gradient(90deg, #22c55e, #84cc16, #eab308);
  border-radius: 999px;
}

.partner-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.partner-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
  padding: 14px;
  border-radius: 12px;
}

.partner-item small {
  display: block;
  color: #64748b;
}

.partner-score {
  font-weight: 700;
}

.leaderboard {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.leaderboard-item {
  display: flex;
  align-items: center;
  gap: 14px;
  background: #f8fafc;
  padding: 14px;
  border-radius: 12px;
}

.avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #2563eb;
  color: white;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

.leaderboard-info {
  flex: 1;
}

.leaderboard-info span {
  display: block;
  color: #64748b;
  font-size: 0.85rem;
}

.leaderboard-score {
  font-weight: 700;
  font-size: 1.1rem;
}

.drilldown {
  margin-bottom: 24px;
}

.search-input {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px 14px;
  min-width: 240px;
}

.analytics-table {
  width: 100%;
  border-collapse: collapse;
}

.analytics-table th {
  text-align: left;
  color: #64748b;
  font-weight: 600;
  padding: 14px;
  border-bottom: 1px solid #e2e8f0;
}

.analytics-table td {
  padding: 16px 14px;
  border-bottom: 1px solid #f1f5f9;
}

.score-badge {
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
}

.score-badge.success {
  background: #dcfce7;
  color: #166534;
}

.score-badge.warning {
  background: #fef3c7;
  color: #92400e;
}

.score-badge.neutral {
  background: #dbeafe;
  color: #1e40af;
}

.section-divider {
  margin: 40px 0 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.section-divider::before,
.section-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #cbd5e1;
}

.section-divider span {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 700;
}

@media (max-width: 1200px) {
  .kpi-grid,
  .analytics-grid {
    grid-template-columns: 1fr;
  }
}

.heat-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 4px 3px;
}

.heat-table th {
  text-align: left;
  font-size: 0.85rem;
  color: #64748b;
  padding: 10px;
}

.heat-table td {
  padding: 12px;
  border-radius: 10px;
  font-weight: 600;
}

/* LABEL */
.label {
  background: transparent;
  font-weight: 700;
  color: #0f172a;
}

/* BASE CELL */
.cell {
  text-align: center;
  width: 74px;
}

/* HEAT LEVELS (LOW = GOOD / FAST) */
.heat.low {
  background: rgba(34, 197, 94, 0.15);
  color: #166534;
}

/* MEDIUM */
.heat.mid {
  background: rgba(245, 158, 11, 0.18);
  color: #92400e;
}

/* HIGH = BAD / SLOW */
.heat.high {
  background: rgba(239, 68, 68, 0.18);
  color: #991b1b;
}

/* TOTAL COLUMN */
.total {
  background: #eef2ff;
  color: #1e3a8a;
  font-weight: 800;
}

/* EMPTY */
.muted {
  background: transparent;
  color: #94a3b8;
}
</style>
